/**
 * LoadingSpinner Component
 * An accessible loading spinner with customizable size and message
 */

import React from 'react';
import styled, { keyframes, css } from 'styled-components';

// ============================================================================
// Types & Interfaces
// ============================================================================

export type SpinnerSize = 'sm' | 'md' | 'lg' | 'xl';
export type SpinnerVariant = 'primary' | 'secondary' | 'neutral';

export interface LoadingSpinnerProps {
  /** Size of the spinner */
  size?: SpinnerSize;
  /** Color variant */
  variant?: SpinnerVariant;
  /** Loading message to display */
  message?: string;
  /** Whether to center the spinner */
  centered?: boolean;
  /** Whether to show only the spinner without message */
  minimal?: boolean;
  /** Custom className */
  className?: string;
  /** Custom aria-label for the spinner */
  'aria-label'?: string;
}

// ============================================================================
// Animations
// ============================================================================

const spin = keyframes`
  0% { 
    transform: rotate(0deg); 
  }
  100% { 
    transform: rotate(360deg); 
  }
`;

const fadeIn = keyframes`
  0% { 
    opacity: 0; 
  }
  100% { 
    opacity: 1; 
  }
`;

// ============================================================================
// Styled Components
// ============================================================================

const Container = styled.div<{ $centered: boolean; $minimal: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-md);
  animation: ${fadeIn} 0.2s ease-in-out;

  ${(props) =>
    props.$centered &&
    css`
      justify-content: center;
      min-height: ${props.$minimal ? 'auto' : '200px'};
      padding: ${props.$minimal ? '0' : 'var(--space-xl) var(--space-md)'};
    `}

  ${(props) =>
    !props.$centered &&
    css`
      padding: ${props.$minimal ? '0' : 'var(--space-lg) 0'};
    `}
`;

const Spinner = styled.div<{
  $size: SpinnerSize;
  $variant: SpinnerVariant;
}>`
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
  flex-shrink: 0;

  /* Size variants */
  ${(props) =>
    props.$size === 'sm' &&
    css`
      width: 20px;
      height: 20px;
      border-width: 2px;
    `}

  ${(props) =>
    props.$size === 'md' &&
    css`
      width: 32px;
      height: 32px;
      border-width: 3px;
    `}

  ${(props) =>
    props.$size === 'lg' &&
    css`
      width: 48px;
      height: 48px;
      border-width: 4px;
    `}

  ${(props) =>
    props.$size === 'xl' &&
    css`
      width: 64px;
      height: 64px;
      border-width: 5px;
    `}

  /* Color variants */
  ${(props) =>
    props.$variant === 'primary' &&
    css`
      border: currentBorderWidth solid var(--brand-50);
      border-top-color: var(--brand-600);
    `}

  ${(props) =>
    props.$variant === 'secondary' &&
    css`
      border: currentBorderWidth solid var(--neutral-100);
      border-top-color: var(--text-muted);
    `}

  ${(props) =>
    props.$variant === 'neutral' &&
    css`
      border: currentBorderWidth solid var(--border);
      border-top-color: var(--text-primary);
    `}

  /* Respect user's motion preferences */
  @media (prefers-reduced-motion: reduce) {
    animation: none;

    /* Show a static loading indicator instead */
    &::after {
      content: '⏳';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      font-size: ${(props) => {
        switch (props.$size) {
          case 'sm':
            return '12px';
          case 'md':
            return '16px';
          case 'lg':
            return '24px';
          case 'xl':
            return '32px';
          default:
            return '16px';
        }
      }};
    }

    border: none;
  }
`;

const Message = styled.p<{ $size: SpinnerSize }>`
  margin: 0;
  color: var(--text-muted);
  text-align: center;
  font-weight: var(--font-weight-medium);

  ${(props) =>
    props.$size === 'sm' &&
    css`
      font-size: var(--font-size-sm);
    `}

  ${(props) =>
    props.$size === 'md' &&
    css`
      font-size: var(--font-size-base);
    `}

  ${(props) =>
    props.$size === 'lg' &&
    css`
      font-size: var(--font-size-lg);
    `}

  ${(props) =>
    props.$size === 'xl' &&
    css`
      font-size: var(--font-size-xl);
    `}
`;

// ============================================================================
// Component
// ============================================================================

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 'md',
  variant = 'primary',
  message = 'Cargando...',
  centered = true,
  minimal = false,
  className,
  'aria-label': ariaLabel,
}) => {
  const defaultAriaLabel = message || 'Cargando contenido';

  return (
    <Container
      $centered={centered}
      $minimal={minimal}
      className={className}
      role="status"
      aria-live="polite"
      aria-label={ariaLabel || defaultAriaLabel}
    >
      <Spinner
        $size={size}
        $variant={variant}
        aria-hidden="true"
        data-testid="loading-spinner-visual"
      />

      {!minimal && message && <Message $size={size}>{message}</Message>}

      {/* Screen reader announcement */}
      <span className="sr-only">{ariaLabel || defaultAriaLabel}</span>
    </Container>
  );
};

LoadingSpinner.displayName = 'LoadingSpinner';

// ============================================================================
// Preset Configurations
// ============================================================================

/** Small inline spinner for buttons or small spaces */
export const InlineSpinner: React.FC<
  Omit<LoadingSpinnerProps, 'size' | 'centered' | 'minimal'>
> = (props) => (
  <LoadingSpinner {...props} size="sm" centered={false} minimal={true} />
);

/** Full page loading spinner */
export const FullPageSpinner: React.FC<
  Omit<LoadingSpinnerProps, 'size' | 'centered'>
> = (props) => <LoadingSpinner {...props} size="lg" centered={true} />;

/** Card content loading spinner */
export const CardSpinner: React.FC<
  Omit<LoadingSpinnerProps, 'size' | 'centered'>
> = (props) => <LoadingSpinner {...props} size="md" centered={true} />;
